import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { notificationService } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';


const NotificationPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [serviceAvailable, setServiceAvailable] = useState(true);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return;
    loadNotifications();

    let channel = null;
    try {
      channel = notificationService?.subscribeToNotifications(user?.id, (newNotif) => {
        setNotifications(prev => [newNotif, ...prev]);
        setUnreadCount(prev => prev + 1);
      });
    } catch (err) {
      // Subscription failed silently
    }

    return () => {
      try {
        notificationService?.unsubscribe(channel);
      } catch (err) {
        // Cleanup failed silently
      }
    };
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef?.current && !panelRef?.current?.contains(e?.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const [notifData, unreadData] = await Promise.all([
        notificationService?.getMyNotifications(),
        notificationService?.getUnreadCount()
      ]);
      // Check if the service returned an error indicating table doesn't exist
      if (notifData?.error?.code === '42P01' || notifData?.error?.message?.includes('does not exist') || notifData?.error?.message?.includes('schema cache')) {
        setServiceAvailable(false);
        return;
      }
      setNotifications(notifData?.data || []);
      setUnreadCount(unreadData?.count || 0);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setServiceAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notifId) => {
    try {
      await notificationService?.markAsRead(notifId);
      setNotifications(prev => prev?.map(n => n?.id === notifId ? { ...n, isRead: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      // Silent fail
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService?.markAllAsRead();
      setNotifications(prev => prev?.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      // Silent fail
    }
  };

  const handleNotificationClick = (notif) => {
    if (!notif?.isRead) handleMarkAsRead(notif?.id);
    if (notif?.projectId) {
      navigate(`/project-detail-management/${notif?.projectId}`);
      setIsOpen(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (err) {
      return '';
    }
  };

  // If service is unavailable, render a disabled bell without crashing
  if (!serviceAvailable) {
    return (
      <div className="relative">
        <button
          className="relative p-2 rounded-lg text-muted-foreground opacity-50 cursor-default"
          title="Notifications unavailable"
          disabled
        >
          <Icon name="Bell" size={20} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="relative p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
        title="Notifications"
      >
        <Icon name="Bell" size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-xl shadow-xl z-[200] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="Bell" size={16} className="text-accent" />
              <span className="text-sm font-semibold text-foreground">Notifications</span>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">{unreadCount}</span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-accent hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent"></div>
              </div>
            ) : notifications?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                <Icon name="BellOff" size={28} className="text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
              </div>
            ) : (
              notifications?.map((notif) => (
                <button
                  key={notif?.id}
                  onClick={() => handleNotificationClick(notif)}
                  className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-muted/50 transition-colors ${
                    !notif?.isRead ? 'bg-accent/5' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      notif?.type === 'project_assignment' ? 'bg-amber-100' : 'bg-blue-100'
                    }`}>
                      <Icon
                        name={notif?.type === 'project_assignment' ? 'Briefcase' : 'Bell'}
                        size={14}
                        className={notif?.type === 'project_assignment' ? 'text-amber-600' : 'text-blue-600'}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold text-foreground leading-tight ${
                        !notif?.isRead ? 'font-bold' : ''
                      }`}>{notif?.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed line-clamp-2">{notif?.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">{formatTime(notif?.createdAt)}</p>
                    </div>
                    {!notif?.isRead && (
                      <div className="w-2 h-2 rounded-full bg-accent flex-shrink-0 mt-1"></div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
